import axios from 'axios';
import { API_BASE_URI } from 'library/constants/api';

const PROPERTY_API_BASE_URL = `${API_BASE_URI}/property`;
const BOOKING_API_BASE_URL = `${API_BASE_URI}/booking`;

export interface CancellationRefundPreview {
  [key: string]: any;
}

export interface InitiateRefundRequest {
  bookingId: string;
}

export interface OfferDiscountRequest {
  propertyId: number;
  userId: number;
  checkIn: string;
  checkOut: string;
  discountAmount: number;
  adults: number;
  children?: number;
  pets?: number;
}

export interface OfferDiscountResponse {
  responseInformation: {
    responseCode: number;
    responseMessage: string;
  };
  result?: {
    paymentLink?: string;
    bookingId?: string;
    /**
     * What the guest will actually be charged — the figure the payment link
     * collects, from the server's own pricing. Use it for the offer card rather
     * than a client-side estimate, which omits tax, fees and calendar pricing.
     */
    totalCustomerPayable?: number;
    discountAmount?: number;
    [key: string]: any;
  };
}

interface BookingSettingsData {
  [key: string]: any;
}

interface BookingServiceResponse {
  [key: string]: any;
}

const BookingService = {
  savePropertyBookingSettings: async (
    data: BookingSettingsData,
  ): Promise<BookingServiceResponse> => {
    const headers = {
      Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
    };
    try {
      const response = await axios
        .post(`${PROPERTY_API_BASE_URL}/savePropertyBookingSettings`, data, {
          headers,
        })
        .catch((error) => {
          if (error?.response?.status === 403) {
            localStorage.clear();
            sessionStorage.clear();
            window.open('/sign-in', '_self');
          }
        });
      return response?.data || {};
    } catch (error) {
      console.error('Error posting data:', error);
      throw error;
    }
  },

  getCancellationRefund: async (bookingId: string): Promise<CancellationRefundPreview> => {
    const headers = {
      Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
      'Content-Type': 'application/json',
    };
    const response = await axios.get(
      `${BOOKING_API_BASE_URL}/getCancellationRefund?bookingId=${encodeURIComponent(bookingId)}`,
      { headers },
    );
    return response.data;
  },

  createCancellation: async (data: InitiateRefundRequest): Promise<any> => {
    const headers = {
      Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
      'Content-Type': 'application/json',
    };
    const response = await axios.post(
      `${BOOKING_API_BASE_URL}/createCancellation`,
      data,
      { headers },
    );
    return response.data;
  },

  getBookingPriceBreakup: async (page: number, size: number, search?: string, dateFrom?: string, dateTo?: string, pendingOnly?: boolean): Promise<any> => {
    const headers = {
      Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
      'Content-Type': 'application/json',
    };
    const params = new URLSearchParams({ page: String(page), size: String(size) });
    if (search?.trim()) params.append('search', search.trim());
    if (dateFrom) params.append('dateFrom', dateFrom);
    if (dateTo) params.append('dateTo', dateTo);
    if (pendingOnly) params.append('pendingOnly', 'true');
    const response = await axios.get(
      `${BOOKING_API_BASE_URL}/getBookingPriceBreakup?${params.toString()}`,
      { headers },
    );
    return response.data;
  },

  getVerificationDocumentUrl: async (bookingId: string): Promise<{ responseInformation: any; result?: string }> => {
    const headers = {
      Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
      'Content-Type': 'application/json',
    };
    const response = await axios.get(
      `${BOOKING_API_BASE_URL}/getVerificationDocumentUrl?bookingId=${encodeURIComponent(bookingId)}`,
      { headers },
    );
    return response.data;
  },

  getSoulpressoBookingList: async (page: number, size: number, search?: string): Promise<any> => {
    const headers = {
      Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
      'Content-Type': 'application/json',
    };
    const params = new URLSearchParams({ page: String(page), size: String(size) });
    if (search?.trim()) params.append('search', search.trim());
    const response = await axios.get(
      `${BOOKING_API_BASE_URL}/getSoulpressoBookingList?${params.toString()}`,
      { headers },
    );
    return response.data;
  },

  getBysBookingsExport: async (dateFrom: string, dateTo: string): Promise<any> => {
    const headers = {
      Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
      'Content-Type': 'application/json',
    };
    const params = new URLSearchParams({ dateFrom, dateTo });
    const response = await axios.get(
      `${BOOKING_API_BASE_URL}/getBysBookingsExport?${params.toString()}`,
      { headers },
    );
    return response.data;
  },

  getSoulpressoBookingsExport: async (dateFrom: string, dateTo: string): Promise<any> => {
    const headers = {
      Authorization: `Bearer ${localStorage.getItem('jwtToken')}`,
      'Content-Type': 'application/json',
    };
    const params = new URLSearchParams({ dateFrom, dateTo });
    const response = await axios.get(
      `${BOOKING_API_BASE_URL}/getSoulpressoBookingsExport?${params.toString()}`,
      { headers },
    );
    return response.data;
  },

  getBookingAdminAccess: async (): Promise<{ isBysAdmin: boolean; isSoulpressoAdmin: boolean }> => {
    const headers = { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` };
    const response = await axios.get(`${BOOKING_API_BASE_URL}/getBookingAdminAccess`, { headers });
    return response.data;
  },

  offerDiscountForBooking: async (
    data: OfferDiscountRequest,
    token: string,
  ): Promise<OfferDiscountResponse> => {
    const headers: any = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    };
    const response = await axios.post(
      `${BOOKING_API_BASE_URL}/offerDiscountForBooking`,
      data,
      { headers },
    );
    return response.data;
  },

  retractOfferForBooking: async (
    bookingId: string,
    token: string,
  ): Promise<{ responseInformation: { responseCode: number; responseMessage?: string }; result?: string }> => {
    const response = await axios.post(
      `${BOOKING_API_BASE_URL}/retractOfferForBooking`,
      null,
      { params: { bookingId }, headers: { Authorization: `Bearer ${token}` } },
    );
    return response.data;
  },
};

export default BookingService;
